package domain.cards

import domain.levels.RoomObjective
import utils.toStringOneDecimal

/**
 * Single source of truth for all card templates.
 *
 * Uses the hardcoded [BUILTIN_TEMPLATES] as the sole source.
 *
 * ## Scaling
 * When [drawOptions] is called with a `level`, every template's
 * [CardTemplate.scalingObjective] and [CardTemplate.scalingGarbage] envelopes
 * are evaluated at that level to produce concrete [CardDefinition]s. The
 * description's `{OBJ}` placeholder is replaced with the computed value.
 *
 * ## Uniqueness
 * Templates marked [CardTemplate.unique] are excluded from the draw pool once
 * an active card with the same id exists in [RunState.activeCards].
 */
object CardRepository {

    val templates: List<CardTemplate> get() = BUILTIN_TEMPLATES

    // ── Draw API ──────────────────────────────────────────────────────────────

    /**
     * Draw [count] distinct card options for the pick screen at room [level].
     */
    fun drawOptions(
        count: Int = 3,
        level: Int = 1,
        runState: RunState? = null,
    ): List<CardDefinition> {
        val activePieceSetKey = runState?.activePieceSet?.pieceSetKey
        val activeIds = runState?.activeCards?.map { it.definition.id }?.toSet() ?: emptySet()
        val hasNonTetrominoes = activePieceSetKey != null && activePieceSetKey != "TETROMINO"

        val eligible = BUILTIN_TEMPLATES.filter { t ->
            if (t.unique && t.id in activeIds) return@filter false

            val pieceEffect = t.effects.filterIsInstance<CardEffect.SetCustomPieceSet>().firstOrNull()
            if (pieceEffect != null && pieceEffect.pieceSetKey == activePieceSetKey) return@filter false

            if (t.effects.any { it is CardEffect.RestoreTetrominoes } && !hasNonTetrominoes) return@filter false

            true
        }

        val weight: (CardTemplate) -> Int = { t ->
            val rarityW = when (t.rarity) {
                CardRarity.COMMON -> if (level < 4) 35 else 15
                CardRarity.RARE -> 20
                CardRarity.EPIC -> if (level >= 3) 20 else 8
                CardRarity.LEGENDARY -> if (level >= 6) 14 else 4
            }
            val categoryBonus = if (t.category == CardCategory.OBJECTIVE) 5 else 0
            rarityW + categoryBonus
        }

        val pool = eligible.flatMap { t -> List(weight(t)) { t } }
        val drawn = pool.shuffled().distinctBy { it.id }.take(count)

        return drawn.map { t -> instantiate(t, level) }
    }

    // ── Instantiation ─────────────────────────────────────────────────────────

    fun instantiate(template: CardTemplate, level: Int): CardDefinition {
        val scaledObjValue = template.scalingObjective?.at(level)
        val scaledGarbageLines = template.scalingGarbage?.at(level)?.toInt()

        val scaledEffects = template.effects.map { effect ->
            when {
                scaledObjValue != null && effect is CardEffect.SetRoomObjective -> {
                    CardEffect.SetRoomObjective(effect.objective.withTarget(scaledObjValue))
                }
                scaledGarbageLines != null && effect is CardEffect.SpawnBlockedGarbage -> {
                    effect.copy(initialLines = scaledGarbageLines)
                }
                else -> effect
            }
        }

        val displayObj = scaledObjValue?.let { formatObjValue(it) } ?: ""
        val description = template.description.replace("{OBJ}", displayObj)

        return CardDefinition(
            id = template.id,
            name = template.name,
            description = description,
            flavourText = template.flavourText,
            category = template.category,
            rarity = template.rarity,
            target = template.target,
            effects = scaledEffects,
            iconKey = template.iconKey,
            durationRounds = template.durationRounds,
            coinCost = template.coinCost,
            unique = template.unique,
        )
    }

    private fun formatObjValue(v: Double): String = when {
        v >= 10_000 -> "${(v / 1000).toInt()}K"
        v == v.toLong().toDouble() -> "${v.toLong()}"
        else -> v.toStringOneDecimal()
    }
}

// ── Extension: clone objective with new target ────────────────────────────────

private fun RoomObjective.withTarget(t: Double): RoomObjective = when (this) {
    is RoomObjective.Lines -> copy(target = t)
    is RoomObjective.Score -> copy(target = t)
    is RoomObjective.DifficultClears -> copy(target = t)
    is RoomObjective.Spins -> copy(target = t)
    is RoomObjective.MaxCombo -> copy(target = t)
    is RoomObjective.PieceSurvival -> copy(target = t)
}

// ── Builtin templates ─────────────────────────────────────────────────────────

private val BUILTIN_TEMPLATES: List<CardTemplate> = listOf(

    // ── COMMON ────────────────────────────────────────────────────────────────

    CardTemplate(
        id = "speed_tax",
        name = "Speed Tax",
        description = "Gravity ×1.5. Clear {OBJ} lines to advance. Score ×1.5.",
        flavourText = "Faster pieces, fatter pockets.",
        category = CardCategory.GRAVITY,
        rarity = CardRarity.COMMON,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetGravityMultiplier(1.5),
            CardEffect.SetScoreMultiplier(1.5),
            CardEffect.SetRoomObjective(RoomObjective.Lines(8.0)),
        ),
        iconKey = "gravity_up",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 8.0, perRoom = 1.0, min = 8.0, max = 18.0),
    ),

    CardTemplate(
        id = "drizzle",
        name = "Drizzle",
        description = "{OBJ} garbage lines spawn. Score ×1.3.",
        flavourText = "A light shower never hurt anyone.",
        category = CardCategory.GARBAGE,
        rarity = CardRarity.COMMON,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SpawnBlockedGarbage(initialLines = 3, columnShiftInterval = 4),
            CardEffect.SetScoreMultiplier(1.3),
            CardEffect.RequireGarbageClear,
        ),
        iconKey = "garbage",
        durationRounds = null,
        unique = false,
        scalingGarbage = ScaleEnvelope(base = 3.0, perRoom = 0.5, min = 3.0, max = 7.0),
    ),

    CardTemplate(
        id = "double_down",
        name = "Double Down",
        description = "Score ×2 this room. No other changes.",
        flavourText = "No risk, just reward. For now.",
        category = CardCategory.MODIFIER,
        rarity = CardRarity.COMMON,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetScoreMultiplier(2.0),
        ),
        iconKey = "score_up",
        durationRounds = 1,
        unique = false,
    ),

    CardTemplate(
        id = "coin_press",
        name = "Coin Press",
        description = "Coins ×2 for {OBJ} rooms.",
        flavourText = "The mint never closes.",
        category = CardCategory.MODIFIER,
        rarity = CardRarity.COMMON,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetCoinMultiplier(2.0),
        ),
        iconKey = "coin",
        durationRounds = 3,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 3.0, perRoom = 0.0),
    ),

    // ── RARE ──────────────────────────────────────────────────────────────────

    CardTemplate(
        id = "orbital_decay",
        name = "Orbital Decay",
        description = "Land {OBJ} spin clears to advance.",
        flavourText = "Elegance is its own currency.",
        category = CardCategory.OBJECTIVE,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetRoomObjective(RoomObjective.Spins(2.0)),
        ),
        iconKey = "spin",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 2.0, perRoom = 1.0, min = 2.0, max = 8.0),
    ),

    CardTemplate(
        id = "event_horizon_rare",
        name = "Event Horizon",
        description = "Score {OBJ} points to advance.",
        flavourText = "Points make the world go round.",
        category = CardCategory.OBJECTIVE,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetRoomObjective(RoomObjective.Score(8_000.0)),
        ),
        iconKey = "score_target",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 8_000.0, perRoom = 2_000.0, min = 8_000.0, max = 40_000.0),
    ),

    CardTemplate(
        id = "blind_spot",
        name = "Blind Spot",
        description = "Preview shrinks to 1 piece. Score ×2.",
        flavourText = "Trust your instincts.",
        category = CardCategory.PIECES,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetPreviewSize(1),
            CardEffect.SetScoreMultiplier(2.0),
        ),
        iconKey = "half_sight",
        durationRounds = null,
        unique = true,
    ),

    CardTemplate(
        id = "difficult_terrain",
        name = "Difficult Terrain",
        description = "Land {OBJ} difficult clears to advance.",
        flavourText = "If it's easy, it doesn't count.",
        category = CardCategory.OBJECTIVE,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetRoomObjective(RoomObjective.DifficultClears(3.0)),
        ),
        iconKey = "diff_clear",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 3.0, perRoom = 1.0, min = 3.0, max = 10.0),
    ),

    CardTemplate(
        id = "dead_weight",
        name = "Dead Weight",
        description = "Hold piece is disabled. Score ×2.5.",
        flavourText = "Commitment is the highest skill.",
        category = CardCategory.PIECES,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetHoldEnabled(false),
            CardEffect.SetScoreMultiplier(2.5),
        ),
        iconKey = "no_hold",
        durationRounds = null,
        unique = true,
    ),

    CardTemplate(
        id = "gravity_well",
        name = "Gravity Well",
        description = "Gravity ×2. Score ×2. Clear {OBJ} lines.",
        flavourText = "The deeper you go, the harder it pulls.",
        category = CardCategory.GRAVITY,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetGravityMultiplier(2.0),
            CardEffect.SetScoreMultiplier(2.0),
            CardEffect.SetRoomObjective(RoomObjective.Lines(8.0)),
        ),
        iconKey = "gravity_up",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 8.0, perRoom = 1.0, min = 8.0, max = 20.0),
    ),

    CardTemplate(
        id = "survive_the_flood",
        name = "Survive the Flood",
        description = "Gravity 5.0X. Survive {OBJ} pieces without topping out.",
        flavourText = "Every placement is a prayer.",
        category = CardCategory.OBJECTIVE,
        rarity = CardRarity.RARE,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetGravityMultiplier(5.0),
            CardEffect.SetRoomObjective(RoomObjective.PieceSurvival(50.0)),
        ),
        iconKey = "survival",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 50.0, perRoom = 8.0, min = 50.0, max = 120.0),
    ),

    // ── EPIC ──────────────────────────────────────────────────────────────────

    CardTemplate(
        id = "pentomino_pact",
        name = "Pentomino Pact",
        description = "Pieces become pentominoes. Score ×50. Clear {OBJ} lines.",
        flavourText = "Why play with 4 when you can play with 5?",
        category = CardCategory.PIECES,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetCustomPieceSet(pieceSetKey = "PENTO", bagType = "SEVEN"),
            CardEffect.SetScoreMultiplier(50.0),
            CardEffect.SetRoomObjective(RoomObjective.Lines(8.0)),
        ),
        iconKey = "joker",
        durationRounds = null,
        unique = true,
        scalingObjective = ScaleEnvelope(base = 8.0, perRoom = 1.0, min = 8.0, max = 16.0),
    ),

    CardTemplate(
        id = "tromino_trial",
        name = "Tromino Trial",
        description = "Pieces become trominoes. Gravity ×2. Score ×2. Survive {OBJ} pieces.",
        flavourText = "Small bricks, tall walls.",
        category = CardCategory.PIECES,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetCustomPieceSet(pieceSetKey = "TROMINO", bagType = "SEVEN"),
            CardEffect.SetGravityMultiplier(2.0),
            CardEffect.SetScoreMultiplier(2.0),
            CardEffect.SetRoomObjective(RoomObjective.PieceSurvival(60.0)),
        ),
        iconKey = "brick",
        durationRounds = null,
        unique = true,
        scalingObjective = ScaleEnvelope(base = 60.0, perRoom = 6.0, min = 60.0, max = 120.0),
    ),

    CardTemplate(
        id = "instant_gravity",
        name = "Instant Gravity",
        description = "Pieces lock on touch. Gravity ×0.7. Land {OBJ} difficult clears. Score ×1.2.",
        flavourText = "The floor doesn't forgive hesitation.",
        category = CardCategory.GRAVITY,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetLockDelay(0.0),
            CardEffect.SetGravityMultiplier(0.85),
            CardEffect.SetScoreMultiplier(1.2),
            CardEffect.SetRoomObjective(RoomObjective.DifficultClears(3.0)),
        ),
        iconKey = "sticky",
        durationRounds = null,
        unique = true,
        scalingObjective = ScaleEnvelope(base = 3.0, perRoom = 1.0, min = 3.0, max = 8.0),
    ),

    CardTemplate(
        id = "buried_alive",
        name = "Buried Alive",
        description = "{OBJ} blocked lines appear. Score {OBJ2} pts to advance. Score ×2.5.",
        flavourText = "Dig or die.",
        category = CardCategory.GARBAGE,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SpawnBlockedGarbage(initialLines = 8, columnShiftInterval = 2),
            CardEffect.SetScoreMultiplier(2.5),
            CardEffect.SetRoomObjective(RoomObjective.Score(5_000.0)),
            CardEffect.RequireGarbageClear,
        ),
        iconKey = "garbage_wall",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 5_000.0, perRoom = 1_500.0, min = 5_000.0, max = 25_000.0),
        scalingGarbage = ScaleEnvelope(base = 8.0, perRoom = 1.0, min = 8.0, max = 14.0),
    ),

    CardTemplate(
        id = "triple_threat",
        name = "Triple Threat",
        description = "Score ×3. Coins ×3. Gravity ×1.5.",
        flavourText = "Power has a price. Here it's speed.",
        category = CardCategory.MODIFIER,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetScoreMultiplier(3.0),
            CardEffect.SetCoinMultiplier(3.0),
            CardEffect.SetGravityMultiplier(1.5),
        ),
        iconKey = "triple",
        durationRounds = null,
        unique = false,
    ),

    CardTemplate(
        id = "dark_matter",
        name = "Dark Matter",
        description = "Preview hidden entirely. Score ×4.",
        flavourText = "You never needed to see the future.",
        category = CardCategory.PIECES,
        rarity = CardRarity.EPIC,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetPreviewSize(0),
            CardEffect.SetScoreMultiplier(4.0),
        ),
        iconKey = "dark_matter",
        durationRounds = null,
        unique = true,
    ),

    // ── LEGENDARY ─────────────────────────────────────────────────────────────

    CardTemplate(
        id = "event_horizon",
        name = "Event Horizon",
        description = "TIME IS FROZEN!! Top-out clears the board instead of ending the game. Clear {OBJ} lines to advance.",
        flavourText = "Death is just another respawn.",
        category = CardCategory.BOARD,
        rarity = CardRarity.LEGENDARY,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.FreezeBoardMode(nextLevelLines = 40),
        ),
        iconKey = "freeze",
        durationRounds = 1,
        unique = true,
        scalingObjective = ScaleEnvelope(base = 40.0, perRoom = 2.0, min = 40.0, max = 60.0),
    ),

    CardTemplate(
        id = "coin_singularity",
        name = "Coin Singularity",
        description = "Coins ×5 permanently.",
        flavourText = "Every piece is worth a fortune.",
        category = CardCategory.MODIFIER,
        rarity = CardRarity.LEGENDARY,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetCoinMultiplier(5.0),
        ),
        iconKey = "coin_star",
        durationRounds = null,
        unique = false,
    ),

    CardTemplate(
        id = "void_protocol",
        name = "Void Protocol",
        description = "Gravity ×3. Score ×5. Clear {OBJ} lines.",
        flavourText = "The void moves faster than thought.",
        category = CardCategory.GRAVITY,
        rarity = CardRarity.LEGENDARY,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.SetGravityMultiplier(3.0),
            CardEffect.SetScoreMultiplier(5.0),
            CardEffect.SetRoomObjective(RoomObjective.Lines(10.0)),
        ),
        iconKey = "void",
        durationRounds = null,
        unique = false,
        scalingObjective = ScaleEnvelope(base = 10.0, perRoom = 1.5, min = 10.0, max = 25.0),
    ),

    // ── RESTORE card (special) ────────────────────────────────────────────────

    CardTemplate(
        id = "back_to_basics",
        name = "Back to Basics",
        description = "Revert to standard tetrominoes. Score ×1.5.",
        flavourText = "Sometimes the classics are all you need.",
        category = CardCategory.PIECES,
        rarity = CardRarity.COMMON,
        target = CardTarget.SELF,
        effects = listOf(
            CardEffect.RestoreTetrominoes,
            CardEffect.SetScoreMultiplier(1.5),
        ),
        iconKey = "restore",
        durationRounds = null,
        unique = true,
    ),
)
